import { useEffect, useState } from "react";
import { Shield, Swords, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { SportTabs } from "../components/SportTabs";
import { labelFor } from "../lib/sports";
import { TeamLogo } from "../components/TeamLogo";

const rankColor = (rank, total) => {
  const pct = (rank - 1) / Math.max(1, total - 1);
  return pct < 0.34 ? "#10B981" : pct > 0.66 ? "#EF4444" : "#F59E0B";
};

export default function TeamRankings() {
  const [sport, setSport] = useState("NFL");
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .get("/teams", { params: { sport, source: "live" } })
      .then((r) => setTeams(r.data))
      .finally(() => setLoading(false));
  }, [sport]);

  const positions = teams[0] ? Object.keys(teams[0].def_vs_position) : [];

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-8">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold text-white mb-1">Team Offense & Defense Ranks</h1>
      <p className="text-slate-400 text-sm mb-5">
        Lower rank = stronger. Defense-vs-position shows which spots each team gives up production to.
      </p>

      <div className="mb-5">
        <SportTabs value={sport} onChange={setSport} />
      </div>

      <div className="rounded-2xl border border-[#233248] overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="rankings-loading">
            <Loader2 className="h-6 w-6 animate-spin text-[#00E5FF]" />
          </div>
        ) : teams.length === 0 ? (
          <div data-testid="rankings-empty" className="py-14 px-6 text-center">
            <Shield className="h-8 w-8 text-slate-600 mx-auto mb-3" />
            <h3 className="font-heading text-white font-bold">No {labelFor(sport)} teams loaded yet</h3>
            <p className="text-sm text-slate-400 mt-1 max-w-md mx-auto">
              Head to the Dashboard and sync the {labelFor(sport)} slate — once today's games load, every team's
              offense, defense and defense-vs-position ranks show up here.
            </p>
          </div>
        ) : (
        <table className="w-full text-sm" data-testid="rankings-table">
          <thead>
            <tr className="bg-[#0B0F17] text-slate-400 text-left font-data text-xs">
              <th className="px-4 py-3">Team</th>
              <th className="px-3 py-3"><Swords className="inline h-3.5 w-3.5" /> Off</th>
              <th className="px-3 py-3"><Shield className="inline h-3.5 w-3.5" /> Def</th>
              {teams[0]?.pace && <th className="px-3 py-3">Pace</th>}
              {positions.map((p) => (
                <th key={p} className="px-3 py-3 text-center">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teams.map((t) => (
              <tr key={t.id} className="border-t border-[#233248] hover:bg-[#141C2B]">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <TeamLogo sport={sport} abbr={t.abbr} color={t.color} size="md" />
                    <span className="text-white">{t.name}</span>
                  </div>
                </td>
                <td className="px-3 py-3 font-data font-semibold" style={{ color: rankColor(t.offense_rank, t.total_teams) }}>
                  #{t.offense_rank}
                </td>
                <td className="px-3 py-3 font-data font-semibold" style={{ color: rankColor(t.defense_rank, t.total_teams) }}>
                  #{t.defense_rank}
                </td>
                {t.pace && <td className="px-3 py-3 font-data text-slate-300">{t.pace}</td>}
                {positions.map((p) => (
                  <td key={p} className="px-3 py-3 text-center font-data" style={{ color: rankColor(t.def_vs_position[p], t.total_teams) }}>
                    #{t.def_vs_position[p]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        )}
      </div>
    </div>
  );
}
